import {
  LEGACY_OPERATOR_ROLE_TYPE,
  OPERATOR_SUBROLE_TYPES,
  isProductionManagerRole,
  PRODUCTION_MANAGER_ROLE_TYPES,
  SHIPPING_STATION_ROLE_TYPE,
  isSinglePhaseWorkshopStation,
} from './operatorRoles';
import { pickActiveProductionPhase } from '../services/caseProductionPhasesService';
import { workflowActionAllowedForAnyRole } from '../services/workflowRuntimeService';

/**
 * Alias role_type (base ou libellés métier) → clés atelier OPERATOR_SUBROLE_TYPES.
 */
export const WORKSHOP_ROLE_TYPE_ALIASES = {
  operateur_thermoformage: 'operator_thermo',
  operator_thermoformage: 'operator_thermo',
  operateur_thermo: 'operator_thermo',
  operateur_3d: 'operator_3d',
  operator_3df: 'operator_3d',
  operateur_finition: 'operator_finition',
  operateur_qualite: 'operator_qualite',
  operator_qualite_controle: 'operator_qualite',
};

export function normalizeWorkshopRoleType(roleType) {
  if (!roleType || typeof roleType !== 'string') return null;
  const key = roleType.trim();
  return WORKSHOP_ROLE_TYPE_ALIASES[key] || key;
}

export function normalizeWorkshopRoleTypes(roleTypes) {
  const list = Array.isArray(roleTypes) ? roleTypes.filter(Boolean) : [];
  return [...new Set(list.map((r) => normalizeWorkshopRoleType(r)).filter(Boolean))];
}

/** Rôle « poste atelier » à utiliser pour la vue (admin / pilotes avant poste opérateur). */
export function resolvePrimaryWorkshopRole(roleTypes) {
  const list = normalizeWorkshopRoleTypes(roleTypes);
  if (list.includes('administrator')) return 'administrator';
  const pm = list.find((x) => isProductionManagerRole(x) || PRODUCTION_MANAGER_ROLE_TYPES.includes(x));
  if (pm) return pm;
  for (const r of OPERATOR_SUBROLE_TYPES) {
    if (list.includes(r)) return r;
  }
  if (list.includes(SHIPPING_STATION_ROLE_TYPE)) return SHIPPING_STATION_ROLE_TYPE;
  if (list.includes(LEGACY_OPERATOR_ROLE_TYPE)) return LEGACY_OPERATOR_ROLE_TYPE;
  return list[0] || null;
}

/** Sous-rôles atelier présents chez l’utilisateur (ordre métier OPERATOR_SUBROLE_TYPES). */
export function getWorkshopStationRoleTypes(roleTypes) {
  const list = normalizeWorkshopRoleTypes(roleTypes);
  return OPERATOR_SUBROLE_TYPES.filter((r) => list.includes(r));
}

export function isWorkshopOperatorSubrole(roleType) {
  return OPERATOR_SUBROLE_TYPES.includes(roleType);
}

export { isSinglePhaseWorkshopStation } from './operatorRoles';

/**
 * Mots-clés sur phase_name (minuscules) pour rattacher une phase production au poste.
 * Ajustez selon les libellés des phase_templates en base.
 */
export const WORKSHOP_OPERATOR_PHASE_NAME_HINTS = {
  operator_3d: [
    '3d',
    'impression',
    'impression 3d',
    'stl',
    'fabrication',
    'usinage',
    'modél',
    'model',
    'découpe',
    'decoupe',
    'fichier',
    'numerique',
    'numérique',
  ],
  operator_thermo: ['thermo', 'formage', 'thermoform', 'thermoformage', 'goutti'],
  operator_finition: ['finition', 'retouche', 'poliss', 'polish', 'ponç', 'decoupe finition'],
  operator_qualite: ['qualit', 'contrôle', 'controle', 'audit'],
  livraison: [
    'expedition',
    'expédition',
    'livraison',
    'colis',
    'envoi',
    'shipping',
    'emballage',
    'logistique',
    'envoi postal',
    'transport',
  ],
};

export const WORKSHOP_OPERATOR_ROLE_LABELS = {
  operator_3d: 'Opérateur 3D',
  operator_thermo: 'Opérateur thermoformage',
  operator_finition: 'Opérateur finition',
  operator_qualite: 'Opérateur contrôle qualité',
  livraison: 'Livraison / expédition',
};

/** Vue liste complète du parcours (admin / pilotes / ancien rôle operators). */
export function userHasExpandedWorkshopView(userRole) {
  if (!userRole) return true;
  if (userRole === 'administrator') return true;
  if (isProductionManagerRole(userRole)) return true;
  if (PRODUCTION_MANAGER_ROLE_TYPES.includes(userRole)) return true;
  if (userRole === LEGACY_OPERATOR_ROLE_TYPE) return true;
  return !isSinglePhaseWorkshopStation(userRole);
}

/**
 * Phase affichée pour l’opérateur : correspond au poste (mots-clés), sinon phase active du lot.
 */
export function pickWorkshopPhaseForRole(phases, userRole) {
  if (!phases?.length) return null;
  const role = normalizeWorkshopRoleType(userRole) || userRole;
  if (!isSinglePhaseWorkshopStation(role)) {
    return pickActiveProductionPhase(phases);
  }
  const hints = WORKSHOP_OPERATOR_PHASE_NAME_HINTS[role];
  if (!hints?.length) return pickActiveProductionPhase(phases);

  const nameMatch = (p) => {
    const n = String(p.name || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return hints.some((h) => n.includes(h));
  };

  const candidates = phases.filter(nameMatch);
  if (candidates.length === 0) {
    return pickActiveProductionPhase(phases);
  }

  const inProg = candidates.find((p) => p.status === 'in_progress');
  if (inProg) return inProg;

  const pendingIncomplete = candidates.find(
    (p) =>
      p.status === 'pending' &&
      (p.milestones || []).some((m) => !m.completed)
  );
  if (pendingIncomplete) return pendingIncomplete;

  return candidates[0];
}

/**
 * Jalons que l’opérateur peut faire avancer (actions workflow autorisées pour son rôle).
 * Sans action configurée sur le jalon, le jalon reste visible (Confirmer / Rejeter).
 */
/**
 * Index de la phase affichée parmi les phases production du lot (1-based).
 */
export function getPhaseStationIndex(phases, displayPhase) {
  if (!phases?.length || !displayPhase?.id) {
    return { index: 0, total: phases?.length ?? 0 };
  }
  const sorted = [...phases].sort((a, b) => (a.phase_order ?? 0) - (b.phase_order ?? 0));
  const idx = sorted.findIndex((p) => p.id === displayPhase.id);
  return {
    index: idx >= 0 ? idx + 1 : 0,
    total: sorted.length,
  };
}

const WORKSHOP_MILESTONE_INTERACTIVE_ACTIONS = new Set([
  'confirm_milestone',
  'reject_milestone',
  'set_completion_type',
  'open_production_sheet',
  'open_ipr_milestone',
  'add_milestone_note',
  'open_stl_preview',
  'modify_case_files',
]);

export function filterMilestonesForOperator(
  milestones,
  userRoleOrRoles,
  templateActionsByTemplateId,
  workflowDispatchEnabled
) {
  if (!milestones?.length) return [];
  const roles = normalizeWorkshopRoleTypes(
    Array.isArray(userRoleOrRoles)
      ? userRoleOrRoles
      : userRoleOrRoles
        ? [userRoleOrRoles]
        : []
  );
  const primaryRole = roles[0] || null;
  if (!workflowDispatchEnabled || !roles.some((r) => isSinglePhaseWorkshopStation(r))) {
    return milestones;
  }
  return milestones.filter((m) => {
    const tid = m.milestone_template_id;
    if (!tid) return true;
    const rows = templateActionsByTemplateId[tid] || [];
    if (rows.length === 0) return true;
    const allowed = rows.filter((row) =>
      workflowActionAllowedForAnyRole(roles.length ? roles : primaryRole, row.allowed_role_types)
    );
    if (allowed.length > 0) return true;
    if (!m.completed && rows.some((row) => WORKSHOP_MILESTONE_INTERACTIVE_ACTIONS.has(row.action_key))) {
      return true;
    }
    return false;
  });
}
